// @ts-nocheck
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  makeStyles,
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  Icon,
  Button,
  Link,
} from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { DataTable, EmptyState } from '../../../../common/components';
import { useDialog } from '../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../common/utils';
import ServiceMessageForm from './ServiceMessageForm';

const useStyles = makeStyles(() => ({
  container: {
    flex: '1',
    height: '100%',
    minHeight: '500px',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  dialog: {
    padding: '0',
    marginBottom: '0',
  },
  dialogContent: {
    display: 'flex',
    width: '900px',
    maxWidth: '900px',
    overflow: 'hidden',
  },
}));

function ServiceMessageList(props) {
  const { showDialog } = useDialog();
  const [t] = useTranslation();
  const classes = useStyles();

  const {
    data,
    platforms,
    filterParams,
    onCreateServiceMessage,
    onUpdateServiceMessage,
    onFilterChange,
    actionsDisabled,
  } = props;

  const handleShowCreateServiceMessageDialog = () => {
    showDialog({
      title: t('serviceMessage.serviceMessageCreate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <ServiceMessageForm
            platforms={platforms}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            saveButtonText={t('common.create')}
          />
        </Box>
      ),
      onConfirm: onCreateServiceMessage,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const handleShowUpdateServiceMessageDialog = messageData => {
    showDialog({
      title: t('serviceMessage.serviceMessageUpdate'),
      content: params => (
        <Box className={classes.dialogContent}>
          <ServiceMessageForm
            serviceMessage={messageData}
            platforms={platforms}
            onSave={params.onConfirm}
            onCancel={params.onCancel}
            editMode
          />
        </Box>
      ),
      onConfirm: onUpdateServiceMessage,
      className: classes.dialog,
      throwOnError: true,
      hideFooter: true,
    });
  };

  const getSeverity = params => {
    const { value: severity } = params;
    switch (severity) {
      case 1:
      case 2:
        return 'Service besked med recovery';
      default:
        return 'Service besked';
    }
  };

  const getColumnDefs = () => [
    {
      field: 'description',
      headerName: t('serviceMessage.serviceMessageDescription'),
      width: 250,
    },
    {
      field: 'severity',
      headerName: t('serviceMessage.serviceMessageSeverity'),
      width: 250,
      valueGetter: getSeverity,
      sortComparator: StringUtils.compare,
    },
    {
      field: 'affected',
      headerName: t('serviceMessage.serviceMessageAffected'),
      width: 250,
      valueGetter: params => (params.value ? params.value.join(', ') : null),
      sortComparator: StringUtils.compare,
    },
    {
      field: 'state',
      headerName: t('serviceMessage.serviceMessageState'),
      width: 125,
      valueGetter: params => (params.value === 'open' ? 'Åben' : 'Lukket'),
      sortComparator: StringUtils.compare,
    },
    {
      field: 'recoveryEnabled',
      headerName: 'Recovery',
      width: 144,
      valueFormatter: params => (params.value ? 'Aktiveret' : 'Ikke aktiveret'),
    },
    {
      field: 'link',
      headerName: t('serviceMessage.serviceMessageLink'),
      width: 300,
      flex: 1,
      renderCell: params =>
        params.value ? (
          <Link href={params.value} target="_blank" rel="noopener">
            {params.value}
          </Link>
        ) : null,
    },
    {
      field: 'created',
      headerName: t('serviceMessage.serviceMessageCreated'),
      width: 175,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.value),
    },
    {
      field: 'updated',
      headerName: t('serviceMessage.serviceMessageUpdated'),
      width: 175,
      type: 'dateTime',
      valueFormatter: params => DateUtils.getDateTime(params.value),
    },
    {
      field: 'id',
      headerName: t('serviceMessage.serviceMessageActions'),
      width: 125,
      renderCell: params => (
        <Button
          color="primary"
          startIcon={<Icon>edit</Icon>}
          disabled={actionsDisabled}
          onClick={() => handleShowUpdateServiceMessageDialog(params.row)}
        >
          {t('common.edit')}
        </Button>
      ),
    },
  ];

  const getRowData = () => {
    const selectedPlatform = filterParams.platform !== 'all' ? filterParams.platform : null;
    const selectedState = filterParams.state !== 'all' ? filterParams.state : null;
    return _.sortBy(
      data.serviceMessages.filter(
        serviceMessage =>
          (!selectedPlatform || serviceMessage.affected?.includes(selectedPlatform)) &&
          (!selectedState || serviceMessage.state === selectedState)
      ),
      ['severity']
    );
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <TextField
        id="ServiceMessageList-filterPlatforms"
        value={filterParams.platform}
        onChange={event => onFilterChange({ platform: event.target.value })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon>filter_list</Icon>
            </InputAdornment>
          ),
        }}
        select
      >
        <MenuItem value="all">{t('serviceMessage.serviceMessagePlatformsAll')}</MenuItem>
        {platforms.map(platform => (
          <MenuItem key={platform} value={platform}>
            {platform}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        id="ServiceMessageList-filterState"
        value={filterParams.state}
        onChange={event => onFilterChange({ state: event.target.value })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon>filter_list</Icon>
            </InputAdornment>
          ),
        }}
        select
      >
        <MenuItem value="all">{t('serviceMessage.serviceMessageStateOpenAndClosed')}</MenuItem>
        <MenuItem value="open">{t('serviceMessage.serviceMessageStateOpen')}</MenuItem>
        <MenuItem value="closed">{t('serviceMessage.serviceMessageStateClosed')}</MenuItem>
      </TextField>
      <Button
        id="ServiceMessageList-createServiceMessage"
        variant="outlined"
        color="primary"
        startIcon={<Icon>add_comment</Icon>}
        onClick={handleShowCreateServiceMessageDialog}
        disabled={actionsDisabled}
      >
        {t('serviceMessage.serviceMessageCreate')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('serviceMessage.serviceMessagesEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="ServiceMessageList-container" className={classes.container}>
      <DataTable
        id="ServiceMessageList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        pageSize={data.serviceMessages?.length}
        paginationMode="client"
        pagination
      />
    </Box>
  );
}

ServiceMessageList.propTypes = {
  data: PropTypes.shape({
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  platforms: PropTypes.arrayOf(PropTypes.string).isRequired,
  filterParams: PropTypes.shape({
    platform: PropTypes.string,
    state: PropTypes.string,
  }).isRequired,
  onCreateServiceMessage: PropTypes.func.isRequired,
  onUpdateServiceMessage: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
};

export default ServiceMessageList;
